import { useCallback, useEffect, useRef } from 'react';

/*
 * One history stack for the whole app, backed by the browser's.
 *
 * Every top-level screen change pushes a history entry, so the hardware Back button,
 * the browser's back gesture, the in-app edge swipe and every header arrow all do the
 * same thing: go back one screen. The URL itself does not change (we push state only),
 * so nothing about deep links is implied.
 *
 * Screens with an inner layer (a hospital tool, an education viewer) register a back
 * handler; Back closes that layer before leaving the screen. Handlers are a stack, so
 * nested layers close innermost first.
 *
 *   const { push, back, registerBackHandler } = useAppHistory(applyState, currentState)
 *     push(state)                    navigate forward; `applyState(state)` is called and an entry is pushed
 *     back()                         same as the hardware button
 *     registerBackHandler(fn)        fn() returns true if it consumed the Back; returns an unregister function
 */
export const useAppHistory = (applyState, currentState) => {
  const handlers = useRef([]);
  const inPop = useRef(false); // true while a Back is being handled (the stack is mid-change)
  const currentRef = useRef(currentState);
  currentRef.current = currentState;
  const applyRef = useRef(applyState);
  applyRef.current = applyState;

  // Seed the stack on a cold start. The bottom entry is a Home "root"; the app restores
  // the last screen from storage, so a launch can land mid-stack, and that screen then
  // sits above the root so the first Back is a normal pop to Home.
  useEffect(() => {
    const here = currentRef.current;
    const st = window.history.state;
    // Already seeded and in step with the screen being shown: nothing to do. A reload keeps the
    // browser's old state, so if it names a different page than the one restored from storage,
    // re-seed rather than trust it.
    if (st && st.app && (st.app.page === here.page || (st.root && here.page === 'home'))) return;
    window.history.replaceState({ app: { page: 'home' }, root: true }, '');
    if (here.page !== 'home') window.history.pushState({ app: here }, '');
  }, []);

  useEffect(() => {
    const onPop = () => {
      // An inner layer takes the Back if it wants it
      inPop.current = true;
      try {
        for (let i = handlers.current.length - 1; i >= 0; i -= 1) {
          const h = handlers.current[i];
          if (h.fn()) {
            // The pop consumed either the layer's own guard entry (nothing to restore) or a real
            // screen entry, which we re-push so the stack depth is unchanged
            if (h.guard) h.guard = false;
            else window.history.pushState({ app: currentRef.current }, '');
            return;
          }
        }
      } finally {
        inPop.current = false;
      }
      const landed = window.history.state || {};
      if (landed.app) applyRef.current(landed.app);
      else {
        // Walked past our first entry (e.g. opened from a link): go home and stay in the app
        applyRef.current({ page: 'home' });
        window.history.replaceState({ app: { page: 'home' }, root: true }, '');
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const push = useCallback((state) => {
    applyRef.current(state);
    window.history.pushState({ app: state }, '');
  }, []);

  const replace = useCallback((state) => {
    applyRef.current(state);
    window.history.replaceState({ app: state }, '');
  }, []);

  const back = useCallback(() => {
    // Mirror the hardware button exactly: let the browser pop and onPop decide
    window.history.back();
  }, []);

  const registerBackHandler = useCallback((fn) => {
    // A layer opened on the root (Home) has no entry beneath it to pop, so give it one
    const h = { fn, guard: false };
    // A layer registering while a Back is mid-flight sees the root entry only transiently; no guard
    if (!inPop.current && window.history.state && window.history.state.root) {
      window.history.pushState({ app: currentRef.current, guard: true }, '');
      h.guard = true;
    }
    handlers.current.push(h);
    return () => {
      handlers.current = handlers.current.filter((x) => x !== h);
      // Closed by its own control while its guard is still on top: drop the guard
      if (h.guard) { h.guard = false; window.history.back(); }
    };
  }, []);

  return { push, replace, back, registerBackHandler };
};

export default useAppHistory;
